import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { NumericFormat, NumberFormatValues } from "react-number-format"
import {
  createZeroFallbackBlurHandler,
  hasStraySecondDecimalSeparator,
  isDecimalSeparatorPrepended,
  isNoOpAfterNormalization,
  moveCaretAfterDecimal,
  normalizeNumericInputValue,
} from "utils/decimalInput"
import {
  DetailsTrailing,
  StyledInput,
  StyledLabel,
  StyledTextFieldFormItem,
} from "./styled"

interface IProps {
  label: string
  value: number | null
  onChange?: (value: null | number) => void
  placeholder?: string
  detailTrailing?: string
  isError?: boolean
  disabled?: boolean
  readOnly?: boolean
  onFocus?: () => void
  decimalScale?: number
}

const FlatMacroInput = (props: IProps) => {
  const {
    detailTrailing,
    isError = false,
    disabled = false,
    readOnly = false,
    onFocus,
    onChange,
    placeholder,
    value,
    label,
    decimalScale = 0,
  } = props

  const inputMode = decimalScale > 0 ? "decimal" : "numeric"

  const pendingDecimalResetRef = useRef(false)
  const hasInitRef = useRef(false)

  const [internalValue, setInternalValue] = useState(value?.toString() ?? "")

  // Init: populate internalValue once, the first time value is a real number.
  // value can start null/NaN (e.g. async-loaded food data) and become valid after mount.
  useEffect(() => {
    if (hasInitRef.current) return

    const hasNumberValue = value !== null && !isNaN(value)
    if (hasNumberValue) {
      setInternalValue(value.toString())
      hasInitRef.current = true
    }
  }, [value])

  // Update: keep internalValue in sync when value changes externally
  // (e.g. macros recalculated when calories change), without stomping
  // an in-progress edit (the "." reset placeholder).
  useEffect(() => {
    const hasNumberValue = value !== null && !isNaN(value)
    const isValueChanged = value !== Number(internalValue)

    if (hasNumberValue && isValueChanged && internalValue !== ".") {
      setInternalValue(value.toString())
    }
  }, [value])

  const inputRef = useRef<HTMLInputElement>(null)

  const onValueChange = (values: NumberFormatValues) => {
    if (disabled) {
      return
    }

    if (pendingDecimalResetRef.current) {
      pendingDecimalResetRef.current = false
      setInternalValue(".")
      setTimeout(() => moveCaretAfterDecimal(inputRef.current), 20)
      return
    }

    if (values.floatValue !== value) {
      onChange?.(values.floatValue ?? 0)
    }

    const normalized = normalizeNumericInputValue(values.value)

    setInternalValue(normalized)
  }

  const isAllowed = (values: NumberFormatValues) => {
    pendingDecimalResetRef.current = false

    const rawValue = inputRef.current?.value ?? values.value

    if (isDecimalSeparatorPrepended(rawValue, internalValue)) {
      pendingDecimalResetRef.current = true
      return true
    }

    if (hasStraySecondDecimalSeparator(rawValue, internalValue)) {
      return false
    }

    const normalized = normalizeNumericInputValue(values.value)
    return !isNoOpAfterNormalization(values.value, normalized, internalValue)
  }

  const trailingRef = useRef<HTMLDivElement>(null)
  const [trailingWidth, setTrailWidth] = useState(0)

  useLayoutEffect(() => {
    setTrailWidth(trailingRef.current?.getBoundingClientRect().width ?? 0)
  }, [trailingRef, detailTrailing])

  const onBlur = createZeroFallbackBlurHandler({
    value,
    internalValue,
    onChange,
    setInternalValue,
    inputRef,
  })

  return (
    <StyledTextFieldFormItem
      style={
        {
          "--input-padding-right": detailTrailing ? "16px" : "16px",
          "--input-trailing-padding-left": detailTrailing ? "4px" : "0px",
          "--detail-trailing-width": `${trailingWidth}px`,
        } as React.CSSProperties
      }
    >
      <StyledLabel>{label}</StyledLabel>

      <NumericFormat
        allowedDecimalSeparators={[",", "."]}
        isAllowed={isAllowed}
        customInput={StyledInput}
        getInputRef={inputRef}
        inputMode={inputMode}
        decimalScale={decimalScale}
        allowNegative={false}
        thousandSeparator=","
        $disabled={disabled}
        disabled={disabled}
        onFocus={onFocus}
        readOnly={readOnly}
        $isError={isError}
        placeholder={placeholder}
        value={internalValue ?? ""}
        valueIsNumericString
        onValueChange={onValueChange}
        autoComplete="off"
        style={{
          paddingRight:
            "calc(var(--input-padding-right) + var(--input-trailing-padding-left) + var(--detail-trailing-width))",
        }}
        onBlur={onBlur}
      />
      {detailTrailing && <DetailsTrailing>{detailTrailing}</DetailsTrailing>}

      {detailTrailing && (
        <DetailsTrailing style={{ visibility: "hidden" }} ref={trailingRef}>
          {detailTrailing}
        </DetailsTrailing>
      )}
    </StyledTextFieldFormItem>
  )
}

export default FlatMacroInput
